<template>
  <div>


    <b-card> 
      <b-row class="mb-2 pb-2 border-bottom">
        <b-col>
          <b-button
            variant="primary"
            @click="isEventGuestSideBarOpen = true"
          >
            <span class="text-nowrap">Add Attendee</span>
          </b-button>
        </b-col>
      </b-row>

      <b-row v-for="attendee in attendees" :key="attendee.id" class="mb-1 pb-1 border-bottom">
        <div>
            <b-avatar
              :src="attendee.photo_url"
              size="45"
              :variant="`light`"
            />
          </div>
          
          <b-col class="d-flex flex-column ">
            <h4>
              {{ attendee.name }}
            </h4>
            <div class="text-muted">{{ attendee.organisation_name }}</div>
            <small v-if="attendee.is_member" class="text-primary">Member</small>
            <small v-else-if="!attendee.legacy" class="text-secondary">Non-member</small>
            <small v-else class="text-secondary">Legacy Non-member</small>
          </b-col>

        <b-col class="text-right">
          <b-button-group size="sm">
            <b-button 
              variant="outline-primary" 
              v-if="!attendee.is_member && !attendee.legacy"
              @click="editAttendee(attendee)"
            >
              Edit
            </b-button>
            <b-button 
              v-if="!attendee.legacy"
              variant="outline-warning"
              @click="deleteAttendee(attendee)"
            >
              Delete
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Add New Guest Sidebar -->
    <event-edit-guest-add-new
      v-model:is-add-new-guest-sidebar-active="isEventGuestSideBarOpen"
      :attendees="attendees"
      :is-edit-mode="isEditMode"
      :editing-attendee="editingAttendee"
      @guest-added="addGuest"
      @sidebar-closed="onSidebarClosed"
      :all-users="allUsers"
    />
  </div>
</template>

<script>
import formValidation from '@core/comp-functions/forms/form-validation'
import { required } from '@validations'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { BCol, BRow, BCard, BButtonGroup, BButton, BAvatar } from 'bootstrap-vue'
import EventEditGuestAddNew from './EventEditGuestAddNew.vue'

export default {
  components: {
    BCol,
    BRow,
    BCard,
    BButtonGroup,
    BButton,
    BAvatar,
    EventEditGuestAddNew
  },
  props: {
    contentItemData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      isEventGuestSideBarOpen: false,
      isEditMode: false,
      editingAttendee: null,
      required,
      allUsers: [],
      attendees: []
    }
  },
  methods: {
    async addGuest(guest) {
      try {
        if (this.isEditMode && this.editingAttendee) {
          // Update existing non-member attendee
          await store.dispatch('app/updateEventNonMemberAttendee', {
            eventId: this.contentItemData.id,
            attendeeId: this.editingAttendee.id,
            name: guest.name,
            organisationName: guest.org
          })
          
          // Reset edit mode
          this.isEditMode = false
          this.editingAttendee = null
          
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Attendee updated successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else if (guest.isMember) {
          // Add member attendee
          await store.dispatch('app/addEventAttendee', {
            eventId: this.contentItemData.id,
            userId: guest.userId
          })
          
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Attendee added successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          // Add non-member attendee
          await store.dispatch('app/addEventNonMemberAttendee', {
            eventId: this.contentItemData.id,
            name: guest.name,
            organisationName: guest.org
          })
          
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Attendee added successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }

        // Refresh attendees list
        await this.fetchAttendees()

      } catch (error) {
        console.error('Error adding/updating attendee:', error)
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: this.isEditMode ? 'Failed to update attendee' : 'Failed to add attendee',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    editAttendee(attendee) {
      this.editingAttendee = attendee
      this.isEditMode = true
      this.isEventGuestSideBarOpen = true
    },

    onSidebarClosed() {
      // Reset edit mode when sidebar is closed
      this.isEditMode = false
      this.editingAttendee = null
    },

    async deleteAttendee(attendee) {
      try {
        if (attendee.is_member) {
          // Remove member attendee
          await store.dispatch('app/removeEventAttendee', {
            eventId: this.contentItemData.id,
            userId: attendee.id
          })
        } else {
          // Remove non-member attendee
          await store.dispatch('app/removeEventNonMemberAttendee', {
            eventId: this.contentItemData.id,
            attendeeId: attendee.id
          })
        }

        // Refresh attendees list
        await this.fetchAttendees()

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Attendee removed successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error removing attendee:', error)
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to remove attendee',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async fetchAttendees() {
      try {
        if (!this.contentItemData || !this.contentItemData.id) {
          console.warn('Event ID not available yet, skipping attendees fetch')
          return
        }
        
        const response = await store.dispatch('app/fetchEventAttendees', this.contentItemData.id)
        
        this.attendees = response.data.results.attendees || []
      } catch (error) {
        console.error('Error fetching attendees:', error)
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching attendees',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
  watch: {
    // Watch for when contentItemData becomes available and fetch attendees
    'contentItemData.id': {
      handler(newId) {
        if (newId) {
          this.fetchAttendees()
        }
      },
      immediate: true
    }
  },
  computed: {
    // Used to piece together the datetime string
    // from their respective date and time values
    start_datetime() {
      return `${this.start_date?.split('T')?.[0]}T${this.start_time}.000000Z`
    },
    end_datetime() {
      return `${this.end_date?.split('T')?.[0]}T${this.end_time}.000000Z`
    },
    end_min() {
      return new Date(this.start_datetime)
    },
  },
  async mounted() {
    try {
      // Fetch users for the dropdown
      const usersResponse = await store.dispatch('app/fetchDropdownUsers')
      
      this.allUsers = usersResponse.data.results || []

      // Note: fetchAttendees() is called by the watcher when contentItemData.id becomes available
      
      this.loading = false
    } catch (error) {
      console.error('Error loading data:', error)
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Error loading data',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      this.loading = false
    }
  },
  setup() {
    // Use toast
    const toast = useToast()

    const {
      refFormObserver,
      getValidationState,
    } = formValidation()


    return {
      // onSubmit,
      toast,
      refFormObserver,
      getValidationState,
    }
  },

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
